// @givmo/design-system — VideoFacade.
//
// The click-to-play stand-in for a page's embedded video, shared by every Givmo surface that shows
// one (the organization console's "View as public" tab today; the donate page's render next). It
// mounts NOTHING third-party until a person asks for the video:
//
//   * before the click — the block's stored POSTER (an image Givmo serves from its own storage,
//     never fetched from the video host by the browser) under a play control; without a poster, a
//     neutral facade that NAMES the provider ("YouTube video") under the same control. A bare grey
//     box is never shown (Profile Enrichment D-PE-17 (8));
//   * after the click — an iframe on the embed URL, and ONLY when that URL names one of the two
//     canonical embed hosts (plan §3.7). Any other value stays a placeholder and never becomes a
//     frame, whatever the poster says.
import { useState } from 'react';
import { Play } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

// The embed allowlist lives on the React-free `video-embed` subpath so the donate app's CSP builder
// reads the SAME list this facade re-checks; re-exported here for the callers that import them
// beside the component.
import { CANONICAL_VIDEO_EMBED_PREFIXES, isCanonicalVideoEmbedUrl } from '../video-embed';

export { CANONICAL_VIDEO_EMBED_PREFIXES, isCanonicalVideoEmbedUrl };

export interface VideoFacadeProps extends TestIdProps {
  /** The stored embed URL. A frame mounts only on a click, and only when this names an allowed host. */
  embedUrl: string | null;
  /** The stored poster, served by Givmo. Absent ⇒ the neutral facade naming the provider. */
  posterUrl?: string | null;
  /** The provider's name for the neutral facade and the accessible names ("YouTube", "Vimeo"). */
  providerLabel?: string | null;
  /** The block's title, when it has one — the poster's alt text and the frame's title. */
  title?: string | null;
  /** The play control's accessible name. */
  playLabel?: string;
  allowedEmbedPrefixes?: readonly string[];
  className?: string;
}

export function VideoFacade({
  embedUrl,
  posterUrl,
  providerLabel,
  title,
  playLabel = 'Play video',
  allowedEmbedPrefixes = CANONICAL_VIDEO_EMBED_PREFIXES,
  className,
  testId,
}: VideoFacadeProps) {
  const [playing, setPlaying] = useState(false);
  const canPlay = isCanonicalVideoEmbedUrl(embedUrl, allowedEmbedPrefixes);
  const poster = posterUrl?.trim() || null;
  const provider = providerLabel?.trim() || null;
  const videoName = title?.trim() || (provider ? `${provider} video` : 'Video');

  if (playing && canPlay && embedUrl !== null) {
    return (
      <div
        className={cn('aspect-video w-full overflow-hidden rounded-all12 bg-black', className)}
        {...testIdProp(testId)}
      >
        <iframe
          src={embedUrl}
          title={videoName}
          className="h-full w-full"
          allow="accelerometer; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          {...testIdProp(testId ? `${testId}-frame` : undefined)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative aspect-video w-full overflow-hidden rounded-all12 bg-gray80 text-white',
        className,
      )}
      {...testIdProp(testId ? (canPlay ? testId : `${testId}-unavailable`) : undefined)}
    >
      {poster !== null ? (
        <img
          src={poster}
          alt={videoName}
          loading="lazy"
          className="absolute inset-0 size-full object-cover"
          {...testIdProp(testId ? `${testId}-poster` : undefined)}
        />
      ) : (
        <div
          className="absolute inset-0 flex flex-col items-center justify-end gap-8 bg-gradient-to-t from-black/60 to-transparent px-16 pb-16"
          {...testIdProp(testId ? `${testId}-neutral` : undefined)}
        >
          <span className="font-sans text-s14 font-w600 tracking-givmo text-white">
            {videoName}
          </span>
        </div>
      )}
      {canPlay ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`${playLabel}: ${videoName}`}
          className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors hover:bg-black/25 focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white"
          {...testIdProp(testId ? `${testId}-play` : undefined)}
        >
          <span className="flex size-48 items-center justify-center rounded-full bg-white/90 text-black shadow-md">
            <Play size={22} aria-hidden fill="currentColor" />
          </span>
        </button>
      ) : null}
    </div>
  );
}
